import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import desktopLogo from "./assets/deskpos-logo-desktop.png";
import "./BillingDashboardPage.css";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://api.deskpos.ie";

const getAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem(
      "pos_token"
    )}`,
  },
});

const formatMoney = (value) =>
  `€${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleDateString(
    "en-IE"
  );
};

const formatStatus = (status) =>
  String(status || "")
    .replaceAll("_", " ")
    .toUpperCase();

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultInvoiceForm = () => {
  const issueDate = new Date();
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 14);

  return {
    customer_id: "",
    issue_date: toDateInputValue(issueDate),
    due_date: toDateInputValue(dueDate),
    notes: "",
    items: [{ description: "", quantity: "1", unit_price: "" }],
  };
};

const getDefaultCustomerForm = () => ({
  id: null,
  shop_id: "",
  company_name: "",
  contact_name: "",
  billing_email: "",
  phone: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  county: "",
  eircode: "",
  vat_number: "",
  active: true,
});

const getDefaultFilters = () => ({
  period: "all_time",
  from_date: "",
  to_date: "",
  search: "",
  customer_id: "",
  status: "",
});

const getPeriodDates = (period) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  let from = null;
  let to = null;

  if (period === "this_month") {
    from = new Date(year, month, 1);
    to = new Date(year, month + 1, 0);
  } else if (period === "last_month") {
    from = new Date(year, month - 1, 1);
    to = new Date(year, month, 0);
  } else if (period === "this_year") {
    from = new Date(year, 0, 1);
    to = new Date(year, 11, 31);
  }

  return {
    from_date: from ? toDateInputValue(from) : "",
    to_date: to ? toDateInputValue(to) : "",
  };
};

const loadImageAsDataUrl = async (
  imagePath
) => {
  const response = await fetch(imagePath);

  if (!response.ok) {
    throw new Error(
      "Failed to load DeskPOS logo"
    );
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () =>
      resolve(reader.result);

    reader.onerror = () =>
      reject(
        new Error(
          "Failed to prepare DeskPOS logo"
        )
      );

    reader.readAsDataURL(blob);
  });
};

function BillingDashboardPage() {
  const [dashboard, setDashboard] = useState({
    total_invoices: 0,
    paid_invoices: 0,
    outstanding_invoices: 0,
    total_invoiced: 0,
    total_paid: 0,
  });

  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] = useState("");

  const [selectedInvoice, setSelectedInvoice] =
    useState(null);

  const [invoiceLoading, setInvoiceLoading] =
    useState(false);

  const [invoiceError, setInvoiceError] =
    useState("");

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [pdfError, setPdfError] =
    useState("");

  const [paymentFormOpen, setPaymentFormOpen] =
    useState(false);

  const [paymentSaving, setPaymentSaving] =
    useState(false);

  const [paymentError, setPaymentError] =
    useState("");

  const [paymentSuccess, setPaymentSuccess] =
    useState("");

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "bank_transfer",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_reference: "",
    notes: "",
  });

  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [createInvoiceSaving, setCreateInvoiceSaving] = useState(false);
  const [createInvoiceError, setCreateInvoiceError] = useState("");
  const [invoiceForm, setInvoiceForm] = useState(getDefaultInvoiceForm);

  const [customersOpen, setCustomersOpen] = useState(false);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [customerSuccess, setCustomerSuccess] = useState("");
  const [customerForm, setCustomerForm] = useState(getDefaultCustomerForm);
  const [statusActionSaving, setStatusActionSaving] = useState(false);
  const [statusActionError, setStatusActionError] = useState("");
  const [statusActionSuccess, setStatusActionSuccess] = useState("");
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);

  const loadBillingDashboard = useCallback(async (filtersToUse = getDefaultFilters()) => {
    try {
      setLoading(true);
      setError("");

      const filterParams = {
        from_date: filtersToUse.from_date || undefined,
        to_date: filtersToUse.to_date || undefined,
        search: filtersToUse.search?.trim() || undefined,
        customer_id: filtersToUse.customer_id || undefined,
        status: filtersToUse.status || undefined,
      };

      const [dashboardRes, invoicesRes, customersRes] =
        await Promise.all([
          axios.get(
            `${API_URL}/super-admin/billing/dashboard`,
            { ...getAuthConfig(), params: filterParams }
          ),
          axios.get(
            `${API_URL}/super-admin/billing/invoices`,
            { ...getAuthConfig(), params: filterParams }
          ),
          axios.get(
            `${API_URL}/super-admin/billing/customers`,
            getAuthConfig()
          ),
        ]);

      setDashboard(
        dashboardRes.data.dashboard || {}
      );

      setInvoices(
        Array.isArray(
          invoicesRes.data.invoices
        )
          ? invoicesRes.data.invoices
          : []
      );

      setCustomers(
        Array.isArray(customersRes.data.customers)
          ? customersRes.data.customers
          : []
      );
    } catch (err) {
      console.error(
        "Load billing dashboard error:",
        err
      );

      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load billing dashboard"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingDashboard(getDefaultFilters());
  }, [loadBillingDashboard]);

  const openInvoice = async (
    invoiceId
  ) => {
    try {
      setInvoiceLoading(true);
      setInvoiceError("");
      setPdfError("");
      setPaymentError("");
      setPaymentSuccess("");
      setStatusActionError("");
      setStatusActionSuccess("");
      setPaymentFormOpen(false);
      setSelectedInvoice(null);

      const response = await axios.get(
        `${API_URL}/super-admin/billing/invoices/${invoiceId}`,
        getAuthConfig()
      );

      setSelectedInvoice(
        response.data.invoice
      );
    } catch (err) {
      console.error(
        "Load invoice error:",
        err
      );

      setInvoiceError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load invoice"
      );
    } finally {
      setInvoiceLoading(false);
    }
  };

  const closeInvoice = () => {
    setSelectedInvoice(null);
    setInvoiceError("");
    setInvoiceLoading(false);
    setPdfError("");
    setPdfLoading(false);
    setPaymentFormOpen(false);
    setPaymentSaving(false);
    setPaymentError("");
    setPaymentSuccess("");
    setStatusActionError("");
    setStatusActionSuccess("");
  };

  const openPaymentForm = () => {
    setPaymentError("");
    setPaymentSuccess("");
    setPaymentForm({
      amount: Number(selectedInvoice?.balance_due || 0).toFixed(2),
      payment_method: "bank_transfer",
      payment_date: new Date().toISOString().slice(0, 10),
      payment_reference: "",
      notes: "",
    });
    setPaymentFormOpen(true);
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;
    setPaymentForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const recordPayment = async (event) => {
    event.preventDefault();

    if (!selectedInvoice) return;

    try {
      setPaymentSaving(true);
      setPaymentError("");
      setPaymentSuccess("");

      const response = await axios.post(
        `${API_URL}/super-admin/billing/invoices/${selectedInvoice.id}/payments`,
        {
          ...paymentForm,
          amount: Number(paymentForm.amount),
        },
        getAuthConfig()
      );

      const refreshedInvoice = await axios.get(
        `${API_URL}/super-admin/billing/invoices/${selectedInvoice.id}`,
        getAuthConfig()
      );

      setSelectedInvoice(refreshedInvoice.data.invoice);
      setPaymentSuccess(response.data.message || "Payment recorded successfully");
      setPaymentFormOpen(false);
      await loadBillingDashboard(appliedFilters);
    } catch (err) {
      console.error("Record payment error:", err);
      setPaymentError(
        err.response?.data?.error ||
          err.message ||
          "Failed to record payment"
      );
    } finally {
      setPaymentSaving(false);
    }
  };

  const openCreateInvoice = () => {
    const nextForm = getDefaultInvoiceForm();
    const activeCustomers = customers.filter(
      (customer) => customer.active !== false
    );
    if (activeCustomers.length === 1) {
      nextForm.customer_id = String(activeCustomers[0].id);
    }
    setInvoiceForm(nextForm);
    setEditingInvoiceId(null);
    setCreateInvoiceError("");
    setCreateInvoiceOpen(true);
  };

  const closeCreateInvoice = () => {
    if (createInvoiceSaving) return;
    setCreateInvoiceOpen(false);
    setEditingInvoiceId(null);
    setCreateInvoiceError("");
  };

  const handleInvoiceFieldChange = (event) => {
    const { name, value } = event.target;
    setInvoiceForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleInvoiceItemChange = (index, field, value) => {
    setInvoiceForm((previous) => ({
      ...previous,
      items: previous.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addInvoiceItem = () => {
    setInvoiceForm((previous) => ({
      ...previous,
      items: [...previous.items, { description: "", quantity: "1", unit_price: "" }],
    }));
  };

  const removeInvoiceItem = (index) => {
    setInvoiceForm((previous) => ({
      ...previous,
      items: previous.items.length === 1
        ? previous.items
        : previous.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const invoiceFormTotal = invoiceForm.items.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
    0
  );

  const createInvoice = async (event) => {
    event.preventDefault();

    try {
      setCreateInvoiceSaving(true);
      setCreateInvoiceError("");

      const payload = {
        customer_id: Number(invoiceForm.customer_id),
        issue_date: invoiceForm.issue_date,
        due_date: invoiceForm.due_date,
        notes: invoiceForm.notes,
        items: invoiceForm.items.map((item) => ({
          description: item.description,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
          vat_rate: 0,
        })),
      };

      let invoiceId = editingInvoiceId;

      if (editingInvoiceId) {
        await axios.put(
          `${API_URL}/super-admin/billing/invoices/${editingInvoiceId}`,
          payload,
          getAuthConfig()
        );
      } else {
        const response = await axios.post(
          `${API_URL}/super-admin/billing/invoices`,
          payload,
          getAuthConfig()
        );
        invoiceId = response.data.invoice.id;
      }

      setCreateInvoiceOpen(false);
      setEditingInvoiceId(null);
      await loadBillingDashboard(appliedFilters);
      await openInvoice(invoiceId);
    } catch (err) {
      console.error("Create invoice error:", err);
      setCreateInvoiceError(
        err.response?.data?.error || err.message || "Failed to create invoice"
      );
    } finally {
      setCreateInvoiceSaving(false);
    }
  };

  const startEditInvoice = () => {
    if (!selectedInvoice || selectedInvoice.status !== "draft") return;

    setInvoiceForm({
      customer_id: String(selectedInvoice.customer_id || ""),
      issue_date: String(selectedInvoice.issue_date || "").slice(0, 10),
      due_date: selectedInvoice.due_date
        ? String(selectedInvoice.due_date).slice(0, 10)
        : "",
      notes: selectedInvoice.notes || "",
      items: Array.isArray(selectedInvoice.items) && selectedInvoice.items.length
        ? selectedInvoice.items.map((item) => ({
            description: item.description || "",
            quantity: String(item.quantity || "1"),
            unit_price: String(item.unit_price || ""),
          }))
        : [{ description: "", quantity: "1", unit_price: "" }],
    });

    setEditingInvoiceId(selectedInvoice.id);
    setCreateInvoiceError("");
    setSelectedInvoice(null);
    setCreateInvoiceOpen(true);
  };

  const refreshSelectedInvoice = async (invoiceId) => {
    const response = await axios.get(
      `${API_URL}/super-admin/billing/invoices/${invoiceId}`,
      getAuthConfig()
    );
    setSelectedInvoice(response.data.invoice);
  };

  const markInvoiceAsSent = async () => {
    if (!selectedInvoice) return;

    try {
      setStatusActionSaving(true);
      setStatusActionError("");
      setStatusActionSuccess("");

      const response = await axios.patch(
        `${API_URL}/super-admin/billing/invoices/${selectedInvoice.id}/mark-sent`,
        {},
        getAuthConfig()
      );

      await Promise.all([
        refreshSelectedInvoice(selectedInvoice.id),
        loadBillingDashboard(appliedFilters),
      ]);
      setStatusActionSuccess(response.data.message || "Invoice marked as sent");
    } catch (err) {
      console.error("Mark invoice as sent error:", err);
      setStatusActionError(
        err.response?.data?.error || err.message || "Failed to mark invoice as sent"
      );
    } finally {
      setStatusActionSaving(false);
    }
  };

  const cancelInvoice = async () => {
    if (!selectedInvoice) return;

    const confirmed = window.confirm(
      `Cancel ${selectedInvoice.invoice_number}? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setStatusActionSaving(true);
      setStatusActionError("");
      setStatusActionSuccess("");

      const response = await axios.patch(
        `${API_URL}/super-admin/billing/invoices/${selectedInvoice.id}/cancel`,
        {},
        getAuthConfig()
      );

      await Promise.all([
        refreshSelectedInvoice(selectedInvoice.id),
        loadBillingDashboard(appliedFilters),
      ]);
      setStatusActionSuccess(response.data.message || "Invoice cancelled successfully");
    } catch (err) {
      console.error("Cancel invoice error:", err);
      setStatusActionError(
        err.response?.data?.error || err.message || "Failed to cancel invoice"
      );
    } finally {
      setStatusActionSaving(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    if (name === "period") {
      const periodDates = getPeriodDates(value);
      setFilters((previous) => ({
        ...previous,
        period: value,
        ...(value === "custom"
          ? {}
          : periodDates),
      }));
      return;
    }

    setFilters((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "from_date" || name === "to_date"
        ? { period: "custom" }
        : {}),
    }));
  };

  const applyFilters = async (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
    await loadBillingDashboard(filters);
  };

  const clearFilters = async () => {
    const emptyFilters = getDefaultFilters();
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    await loadBillingDashboard(emptyFilters);
  };

  const openCustomers = () => {
    setCustomerError("");
    setCustomerSuccess("");
    setCustomerFormOpen(false);
    setCustomersOpen(true);
  };

  const closeCustomers = () => {
    if (customerSaving) return;
    setCustomersOpen(false);
    setCustomerFormOpen(false);
    setCustomerError("");
    setCustomerSuccess("");
  };

  const startNewCustomer = () => {
    setCustomerForm(getDefaultCustomerForm());
    setCustomerError("");
    setCustomerSuccess("");
    setCustomerFormOpen(true);
  };

  const startEditCustomer = (customer) => {
    setCustomerForm({
      ...getDefaultCustomerForm(),
      ...customer,
      shop_id: customer.shop_id || "",
    });
    setCustomerError("");
    setCustomerSuccess("");
    setCustomerFormOpen(true);
  };

  const handleCustomerChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCustomerForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveCustomer = async (event) => {
    event.preventDefault();

    try {
      setCustomerSaving(true);
      setCustomerError("");
      setCustomerSuccess("");

      const payload = {
        ...customerForm,
        shop_id: customerForm.shop_id || null,
      };

      if (customerForm.id) {
        await axios.put(
          `${API_URL}/super-admin/billing/customers/${customerForm.id}`,
          payload,
          getAuthConfig()
        );
        setCustomerSuccess("Billing customer updated successfully");
      } else {
        await axios.post(
          `${API_URL}/super-admin/billing/customers`,
          payload,
          getAuthConfig()
        );
        setCustomerSuccess("Billing customer created successfully");
      }

      await loadBillingDashboard(appliedFilters);
      setCustomerFormOpen(false);
    } catch (err) {
      console.error("Save billing customer error:", err);
      setCustomerError(
        err.response?.data?.error || err.message || "Failed to save billing customer"
      );
    } finally {
      setCustomerSaving(false);
    }
  };

  const downloadInvoicePdf = async () => {
    if (!selectedInvoice) return;

    try {
      setPdfLoading(true);
      setPdfError("");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const logoData =
        await loadImageAsDataUrl(
          desktopLogo
        );

      const pageWidth =
        doc.internal.pageSize.getWidth();

      const pageHeight =
        doc.internal.pageSize.getHeight();

      const margin = 18;
      const contentWidth =
        pageWidth - margin * 2;

      const navy = [23, 32, 51];
      const brandBlue = [39, 59, 114];
      const muted = [100, 116, 139];
      const light = [245, 247, 251];
      const border = [226, 232, 240];

      const addHeader = () => {
        doc.setFillColor(...brandBlue);
        doc.rect(
          0,
          0,
          pageWidth,
          6,
          "F"
        );

        doc.addImage(
          logoData,
          "PNG",
          margin,
          15,
          48,
          14
        );

        doc.setFont(
          "helvetica",
          "bold"
        );
        doc.setFontSize(22);
        doc.setTextColor(...navy);

        doc.text(
          "INVOICE",
          pageWidth - margin,
          21,
          {
            align: "right",
          }
        );

        doc.setFont(
          "helvetica",
          "normal"
        );
        doc.setFontSize(9);
        doc.setTextColor(...muted);

        doc.text(
          selectedInvoice.invoice_number,
          pageWidth - margin,
          27,
          {
            align: "right",
          }
        );

        doc.setDrawColor(...border);

        doc.line(
          margin,
          36,
          pageWidth - margin,
          36
        );
      };

      addHeader();

      let y = 45;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...navy);

      doc.text("FROM", margin, y);

      doc.text(
        "BILL TO",
        pageWidth / 2 + 5,
        y
      );

      y += 7;

      doc.setFontSize(11);

      doc.text("DeskPOS", margin, y);

      doc.text(
        selectedInvoice.company_name ||
          "-",
        pageWidth / 2 + 5,
        y
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(9);
      doc.setTextColor(...muted);

      const fromLines = [
        "27 Shop Street",
        "Drogheda, Co Louth",
        "info@deskpos.ie",
        "0873634620",
      ];

      const customerLines = [
        selectedInvoice.contact_name,
        selectedInvoice.address_line_1,
        selectedInvoice.address_line_2,
        [
          selectedInvoice.city,
          selectedInvoice.county,
        ]
          .filter(Boolean)
          .join(", "),
        selectedInvoice.eircode,
        selectedInvoice.billing_email,
        selectedInvoice.phone,
      ].filter(Boolean);

      const addressRows = Math.max(
        fromLines.length,
        customerLines.length
      );

      for (
        let index = 0;
        index < addressRows;
        index += 1
      ) {
        y += 5;

        if (fromLines[index]) {
          doc.text(
            fromLines[index],
            margin,
            y
          );
        }

        if (customerLines[index]) {
          doc.text(
            String(
              customerLines[index]
            ),
            pageWidth / 2 + 5,
            y
          );
        }
      }

      y += 12;

      doc.setFillColor(...light);

      doc.roundedRect(
        margin,
        y,
        contentWidth,
        24,
        3,
        3,
        "F"
      );

      const details = [
        {
          label: "Invoice number",
          value:
            selectedInvoice.invoice_number,
          x: margin + 6,
        },
        {
          label: "Issue date",
          value: formatDate(
            selectedInvoice.issue_date
          ),
          x: margin + 55,
        },
        {
          label: "Due date",
          value: formatDate(
            selectedInvoice.due_date
          ),
          x: margin + 96,
        },
        {
          label: "Status",
          value: formatStatus(
            selectedInvoice.status
          ),
          x: margin + 134,
        },
      ];

      details.forEach((detail) => {
        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(8);
        doc.setTextColor(...muted);

        doc.text(
          detail.label,
          detail.x,
          y + 8
        );

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(9);
        doc.setTextColor(...navy);

        doc.text(
          String(detail.value || "-"),
          detail.x,
          y + 16
        );
      });

      y += 34;

      const drawTableHeader = () => {
        doc.setFillColor(...brandBlue);

        doc.rect(
          margin,
          y,
          contentWidth,
          10,
          "F"
        );

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(8);
        doc.setTextColor(
          255,
          255,
          255
        );

        doc.text(
          "DESCRIPTION",
          margin + 4,
          y + 6.5
        );

        doc.text(
          "QTY",
          margin + 105,
          y + 6.5,
          {
            align: "right",
          }
        );

        doc.text(
          "UNIT PRICE",
          margin + 137,
          y + 6.5,
          {
            align: "right",
          }
        );

        doc.text(
          "TOTAL",
          pageWidth - margin - 4,
          y + 6.5,
          {
            align: "right",
          }
        );

        y += 10;
      };

      drawTableHeader();

      const items = Array.isArray(
        selectedInvoice.items
      )
        ? selectedInvoice.items
        : [];

      items.forEach(
        (item, index) => {
          const descriptionLines =
            doc.splitTextToSize(
              String(
                item.description || ""
              ),
              88
            );

          const rowHeight = Math.max(
            11,
            descriptionLines.length *
              4.5 +
              5
          );

          if (
            y + rowHeight >
            pageHeight - 40
          ) {
            doc.addPage();
            addHeader();
            y = 45;
            drawTableHeader();
          }

          if (index % 2 === 0) {
            doc.setFillColor(...light);

            doc.rect(
              margin,
              y,
              contentWidth,
              rowHeight,
              "F"
            );
          }

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(9);
          doc.setTextColor(...navy);

          doc.text(
            descriptionLines,
            margin + 4,
            y + 7
          );

          doc.text(
            Number(
              item.quantity || 0
            ).toFixed(2),
            margin + 105,
            y + 7,
            {
              align: "right",
            }
          );

          doc.text(
            formatMoney(
              item.unit_price
            ),
            margin + 137,
            y + 7,
            {
              align: "right",
            }
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.text(
            formatMoney(
              item.line_total
            ),
            pageWidth - margin - 4,
            y + 7,
            {
              align: "right",
            }
          );

          y += rowHeight;
        }
      );

      if (
        y + 85 >
        pageHeight - 20
      ) {
        doc.addPage();
        addHeader();
        y = 48;
      } else {
        y += 12;
      }

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(8.5);
      doc.setTextColor(...muted);

      doc.text(
        "VAT not charged - supplier is not VAT registered.",
        margin,
        y
      );

      if (selectedInvoice.notes) {
        y += 12;

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(9);
        doc.setTextColor(...navy);

        doc.text("Notes", margin, y);

        y += 5;

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(8.5);
        doc.setTextColor(...muted);

        const noteLines =
          doc.splitTextToSize(
            String(
              selectedInvoice.notes
            ),
            85
          );

        doc.text(
          noteLines,
          margin,
          y
        );
      }

      const totalsX =
        pageWidth - margin - 72;

      const totalsValueX =
        pageWidth - margin;

      const drawTotalRow = (
        label,
        value,
        options = {}
      ) => {
        const {
          bold = false,
          background = false,
        } = options;

        if (background) {
          doc.setFillColor(...light);

          doc.roundedRect(
            totalsX - 5,
            y - 5,
            77,
            11,
            2,
            2,
            "F"
          );
        }

        doc.setFont(
          "helvetica",
          bold ? "bold" : "normal"
        );

        doc.setFontSize(
          bold ? 11 : 9
        );

        doc.setTextColor(...navy);

        doc.text(
          label,
          totalsX,
          y
        );

        doc.text(
          formatMoney(value),
          totalsValueX,
          y,
          {
            align: "right",
          }
        );

        y += bold ? 12 : 9;
      };

      drawTotalRow(
        "Subtotal",
        selectedInvoice.subtotal
      );

      drawTotalRow(
        "Total",
        selectedInvoice.total,
        {
          bold: true,
          background: true,
        }
      );

      drawTotalRow(
        "Paid",
        selectedInvoice.amount_paid
      );

      drawTotalRow(
        "Balance due",
        selectedInvoice.balance_due,
        {
          bold: true,
          background: true,
        }
      );

      const totalPages =
        doc.internal.getNumberOfPages();

      for (
        let pageNumber = 1;
        pageNumber <= totalPages;
        pageNumber += 1
      ) {
        doc.setPage(pageNumber);

        doc.setDrawColor(...border);

        doc.line(
          margin,
          pageHeight - 18,
          pageWidth - margin,
          pageHeight - 18
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(8);
        doc.setTextColor(...muted);

        doc.text(
          "DeskPOS | 27 Shop Street, Drogheda, Co Louth | info@deskpos.ie | 0873634620",
          margin,
          pageHeight - 11
        );

        doc.text(
          `Page ${pageNumber} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 11,
          {
            align: "right",
          }
        );
      }

      doc.save(
        `${selectedInvoice.invoice_number}.pdf`
      );
    } catch (err) {
      console.error(
        "Download invoice PDF error:",
        err
      );

      setPdfError(
        err.message ||
          "Failed to generate invoice PDF"
      );
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="billing-page">
      <div className="billing-header">
        <div>
          <h1>Billing</h1>

          <p>
            Manage DeskPOS customers, invoices and
            payments.
          </p>
        </div>

        <div className="billing-header-actions">
          <button
            type="button"
            className="billing-manage-customers-btn"
            onClick={openCustomers}
          >
            Manage Customers
          </button>

          <button
            type="button"
            className="billing-new-invoice-btn"
            onClick={openCreateInvoice}
          >
            + New Invoice
          </button>
        </div>
      </div>

      {error && (
        <div className="billing-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="billing-loading">
          Loading billing...
        </div>
      ) : (
        <>
          <form className="billing-filters" onSubmit={applyFilters}>
            <div className="billing-filters-grid">
              <label>
                <span>Period</span>
                <select name="period" value={filters.period} onChange={handleFilterChange}>
                  <option value="all_time">All Time</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="custom">Custom Period</option>
                </select>
              </label>

              <label>
                <span>From</span>
                <input type="date" name="from_date" value={filters.from_date} onChange={handleFilterChange} />
              </label>

              <label>
                <span>To</span>
                <input type="date" name="to_date" value={filters.to_date} onChange={handleFilterChange} />
              </label>

              <label>
                <span>Customer</span>
                <select name="customer_id" value={filters.customer_id} onChange={handleFilterChange}>
                  <option value="">All Customers</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.company_name}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Status</span>
                <select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>

              <label className="billing-filter-search">
                <span>Invoice or customer search</span>
                <input type="search" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Invoice number or company" />
              </label>
            </div>

            <div className="billing-filter-actions">
              <button type="button" className="billing-clear-filters-btn" onClick={clearFilters}>Clear</button>
              <button type="submit" className="billing-apply-filters-btn">Apply Filters</button>
            </div>
          </form>

          <div className="billing-summary-grid">
            <div className="billing-summary-card">
              <span>Total Invoiced</span>

              <strong>
                {formatMoney(
                  dashboard.total_invoiced
                )}
              </strong>
            </div>

            <div className="billing-summary-card">
              <span>Total Paid</span>

              <strong>
                {formatMoney(
                  dashboard.total_paid
                )}
              </strong>
            </div>

            <div className="billing-summary-card">
              <span>Total Invoices</span>

              <strong>
                {dashboard.total_invoices ||
                  0}
              </strong>
            </div>

            <div className="billing-summary-card">
              <span>Outstanding</span>

              <strong>
                {dashboard.outstanding_invoices ||
                  0}
              </strong>
            </div>
          </div>

          <div className="billing-card">
            <div className="billing-card-header">
              <div>
                <h2>Recent Invoices</h2>

                <p>
                  Latest DeskPOS customer invoices.
                </p>
              </div>
            </div>

            <div className="billing-table-wrap">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        No invoices found.
                      </td>
                    </tr>
                  ) : (
                    invoices
                      .slice(0, 10)
                      .map((invoice) => (
                        <tr key={invoice.id}>
                          <td>
                            {
                              invoice.invoice_number
                            }
                          </td>

                          <td>
                            {invoice.company_name}
                          </td>

                          <td>
                            {formatDate(
                              invoice.issue_date
                            )}
                          </td>

                          <td>
                            {formatMoney(
                              invoice.total
                            )}
                          </td>

                          <td>
                            {formatMoney(
                              invoice.amount_paid
                            )}
                          </td>

                          <td>
                            {formatMoney(
                              invoice.balance_due
                            )}
                          </td>

                          <td>
                            <span
                              className={`billing-status billing-status-${invoice.status}`}
                            >
                              {formatStatus(
                                invoice.status
                              )}
                            </span>
                          </td>

                          <td>
                            <button
                              type="button"
                              className="billing-view-btn"
                              onClick={() =>
                                openInvoice(
                                  invoice.id
                                )
                              }
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {customersOpen && (
            <div className="billing-modal-overlay">
              <div className="billing-modal billing-customers-modal" role="dialog" aria-modal="true">
                <div className="billing-modal-header">
                  <div><h2>Billing Customers</h2></div>
                  <button type="button" className="billing-modal-close" onClick={closeCustomers}>×</button>
                </div>

                <div className="billing-modal-body">
                  {customerSuccess && <div className="billing-payment-success">{customerSuccess}</div>}
                  {customerError && <div className="billing-error">{customerError}</div>}

                  {!customerFormOpen && (
                    <>
                      <div className="billing-customers-toolbar">
                        <p>{customers.length} billing customer{customers.length === 1 ? "" : "s"}</p>
                        <button type="button" className="billing-new-customer-btn" onClick={startNewCustomer}>+ Add Customer</button>
                      </div>

                      <div className="billing-modal-table-wrap">
                        <table className="billing-modal-table billing-customers-table">
                          <thead><tr><th>Company</th><th>Contact</th><th>Email</th><th>Phone</th><th>Status</th><th>Action</th></tr></thead>
                          <tbody>
                            {customers.length === 0 ? (
                              <tr><td colSpan="6">No billing customers found.</td></tr>
                            ) : customers.map((customer) => (
                              <tr key={customer.id}>
                                <td><strong>{customer.company_name}</strong></td>
                                <td>{customer.contact_name || "-"}</td>
                                <td>{customer.billing_email || "-"}</td>
                                <td>{customer.phone || "-"}</td>
                                <td><span className={`billing-customer-state ${customer.active ? "active" : "inactive"}`}>{customer.active ? "ACTIVE" : "INACTIVE"}</span></td>
                                <td><button type="button" className="billing-edit-customer-btn" onClick={() => startEditCustomer(customer)}>Edit</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {customerFormOpen && (
                    <form onSubmit={saveCustomer}>
                      <div className="billing-customer-form-header">
                        <div><h3>{customerForm.id ? "Edit Customer" : "Add Customer"}</h3><p>Details used on DeskPOS invoices.</p></div>
                        <button type="button" className="billing-payment-cancel" onClick={() => { setCustomerFormOpen(false); setCustomerError(""); }}>Back</button>
                      </div>

                      <div className="billing-customer-form-grid">
                        <label><span>Company name *</span><input name="company_name" value={customerForm.company_name} onChange={handleCustomerChange} required /></label>
                        <label><span>Contact name</span><input name="contact_name" value={customerForm.contact_name || ""} onChange={handleCustomerChange} /></label>
                        <label><span>Billing email</span><input type="email" name="billing_email" value={customerForm.billing_email || ""} onChange={handleCustomerChange} /></label>
                        <label><span>Phone</span><input name="phone" value={customerForm.phone || ""} onChange={handleCustomerChange} /></label>
                        <label><span>Address line 1</span><input name="address_line_1" value={customerForm.address_line_1 || ""} onChange={handleCustomerChange} /></label>
                        <label><span>Address line 2</span><input name="address_line_2" value={customerForm.address_line_2 || ""} onChange={handleCustomerChange} /></label>
                        <label><span>City</span><input name="city" value={customerForm.city || ""} onChange={handleCustomerChange} /></label>
                        <label><span>County</span><input name="county" value={customerForm.county || ""} onChange={handleCustomerChange} /></label>
                        <label><span>Eircode</span><input name="eircode" value={customerForm.eircode || ""} onChange={handleCustomerChange} /></label>
                        <label><span>Customer VAT number</span><input name="vat_number" value={customerForm.vat_number || ""} onChange={handleCustomerChange} /></label>
                        <label className="billing-customer-active-field"><input type="checkbox" name="active" checked={customerForm.active !== false} onChange={handleCustomerChange} /><span>Customer is active</span></label>
                      </div>

                      <div className="billing-create-actions">
                        <button type="button" className="billing-create-cancel-btn" onClick={() => setCustomerFormOpen(false)}>Cancel</button>
                        <button type="submit" className="billing-create-save-btn" disabled={customerSaving}>{customerSaving ? "Saving Customer..." : "Save Customer"}</button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}

          {createInvoiceOpen && (
            <div className="billing-modal-overlay">
              <div className="billing-modal billing-create-invoice-modal" role="dialog" aria-modal="true">
                <div className="billing-modal-header">
                  <div>
                    <h2>{editingInvoiceId ? "Edit Invoice" : "New Invoice"}</h2>
                  </div>
                  <button type="button" className="billing-modal-close" onClick={closeCreateInvoice}>×</button>
                </div>

                <form className="billing-modal-body" onSubmit={createInvoice}>
                  {createInvoiceError && (
                    <div className="billing-error">{createInvoiceError}</div>
                  )}

                  <div className="billing-create-grid">
                    <label className="billing-create-customer-field">
                      <span>Customer</span>
                      <select name="customer_id" value={invoiceForm.customer_id} onChange={handleInvoiceFieldChange} required>
                        <option value="">Select customer</option>
                        {customers
                          .filter((customer) => customer.active !== false)
                          .map((customer) => (
                          <option key={customer.id} value={customer.id}>{customer.company_name}</option>
                        ))}
                      </select>
                    </label>

                    <label>
                      <span>Issue date</span>
                      <input type="date" name="issue_date" value={invoiceForm.issue_date} onChange={handleInvoiceFieldChange} required />
                    </label>

                    <label>
                      <span>Due date</span>
                      <input type="date" name="due_date" value={invoiceForm.due_date} onChange={handleInvoiceFieldChange} />
                    </label>
                  </div>

                  <div className="billing-create-items-header">
                    <div><h3>Invoice Items</h3><p>VAT is not charged.</p></div>
                    <button type="button" className="billing-add-item-btn" onClick={addInvoiceItem}>+ Add Item</button>
                  </div>

                  <div className="billing-create-items">
                    {invoiceForm.items.map((item, index) => (
                      <div className="billing-create-item" key={index}>
                        <label className="billing-item-description">
                          <span>Description</span>
                          <input type="text" value={item.description} onChange={(event) => handleInvoiceItemChange(index, "description", event.target.value)} required />
                        </label>
                        <label>
                          <span>Qty</span>
                          <input type="number" min="0.01" step="0.01" value={item.quantity} onChange={(event) => handleInvoiceItemChange(index, "quantity", event.target.value)} required />
                        </label>
                        <label>
                          <span>Unit price</span>
                          <input type="number" min="0" step="0.01" value={item.unit_price} onChange={(event) => handleInvoiceItemChange(index, "unit_price", event.target.value)} required />
                        </label>
                        <div className="billing-item-line-total"><span>Total</span><strong>{formatMoney(Number(item.quantity || 0) * Number(item.unit_price || 0))}</strong></div>
                        <button type="button" className="billing-remove-item-btn" onClick={() => removeInvoiceItem(index)} disabled={invoiceForm.items.length === 1}>×</button>
                      </div>
                    ))}
                  </div>

                  <label className="billing-create-notes">
                    <span>Notes (optional)</span>
                    <textarea name="notes" rows="3" value={invoiceForm.notes} onChange={handleInvoiceFieldChange} />
                  </label>

                  <div className="billing-create-total"><span>Invoice Total</span><strong>{formatMoney(invoiceFormTotal)}</strong></div>

                  <div className="billing-create-actions">
                    <button type="button" className="billing-create-cancel-btn" onClick={closeCreateInvoice}>Cancel</button>
                    <button type="submit" className="billing-create-save-btn" disabled={createInvoiceSaving || customers.length === 0}>
                      {createInvoiceSaving
                        ? editingInvoiceId
                          ? "Saving Changes..."
                          : "Creating Invoice..."
                        : editingInvoiceId
                          ? "Save Invoice Changes"
                          : "Create Draft Invoice"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {(invoiceLoading ||
            invoiceError ||
            selectedInvoice) && (
            <div
              className="billing-modal-overlay"
              onMouseDown={(event) => {
                if (
                  event.target ===
                  event.currentTarget
                ) {
                  closeInvoice();
                }
              }}
            >
              <div
                className="billing-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Invoice details"
              >
                <div className="billing-modal-header">
                  <div>
                    <h2>
                      {selectedInvoice
                        ? selectedInvoice.invoice_number
                        : "Invoice"}
                    </h2>

                    {selectedInvoice && (
                      <span
                        className={`billing-status billing-status-${selectedInvoice.status}`}
                      >
                        {formatStatus(
                          selectedInvoice.status
                        )}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    className="billing-modal-close"
                    onClick={closeInvoice}
                    aria-label="Close invoice"
                  >
                    ×
                  </button>
                </div>

                {invoiceLoading ? (
                  <div className="billing-modal-message">
                    Loading invoice...
                  </div>
                ) : invoiceError ? (
                  <div className="billing-error billing-modal-error">
                    {invoiceError}

                    <button
                      type="button"
                      className="billing-modal-error-close"
                      onClick={closeInvoice}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  selectedInvoice && (
                    <div className="billing-modal-body">
                      <div className="billing-invoice-details">
                        <div>
                          <h3>Customer</h3>

                          <strong>
                            {
                              selectedInvoice.company_name
                            }
                          </strong>

                          {selectedInvoice.contact_name && (
                            <span>
                              {
                                selectedInvoice.contact_name
                              }
                            </span>
                          )}

                          {selectedInvoice.billing_email && (
                            <span>
                              {
                                selectedInvoice.billing_email
                              }
                            </span>
                          )}

                          {selectedInvoice.phone && (
                            <span>
                              {
                                selectedInvoice.phone
                              }
                            </span>
                          )}

                          {selectedInvoice.address_line_1 && (
                            <span>
                              {
                                selectedInvoice.address_line_1
                              }
                            </span>
                          )}

                          {selectedInvoice.address_line_2 && (
                            <span>
                              {
                                selectedInvoice.address_line_2
                              }
                            </span>
                          )}

                          {(selectedInvoice.city ||
                            selectedInvoice.county) && (
                            <span>
                              {[
                                selectedInvoice.city,
                                selectedInvoice.county,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          )}

                          {selectedInvoice.eircode && (
                            <span>
                              {
                                selectedInvoice.eircode
                              }
                            </span>
                          )}
                        </div>

                        <div>
                          <h3>Invoice Details</h3>

                          <span>
                            <strong>
                              Issue date:
                            </strong>{" "}
                            {formatDate(
                              selectedInvoice.issue_date
                            )}
                          </span>

                          <span>
                            <strong>
                              Due date:
                            </strong>{" "}
                            {formatDate(
                              selectedInvoice.due_date
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="billing-modal-table-wrap">
                        <table className="billing-modal-table">
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Unit price</th>
                              <th>Total</th>
                            </tr>
                          </thead>

                          <tbody>
                            {selectedInvoice.items
                              ?.length ? (
                              selectedInvoice.items.map(
                                (item) => (
                                  <tr key={item.id}>
                                    <td>
                                      {
                                        item.description
                                      }
                                    </td>

                                    <td>
                                      {item.quantity}
                                    </td>

                                    <td>
                                      {formatMoney(
                                        item.unit_price
                                      )}
                                    </td>

                                    <td>
                                      {formatMoney(
                                        item.line_total
                                      )}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td colSpan="4">
                                  No invoice items
                                  found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="billing-invoice-footer">
                        <div className="billing-invoice-notes">
                          {selectedInvoice.notes && (
                            <>
                              <h3>Notes</h3>

                              <p>
                                {
                                  selectedInvoice.notes
                                }
                              </p>
                            </>
                          )}

                          <div className="billing-no-vat">
                            VAT not charged – supplier
                            is not VAT registered.
                          </div>
                        </div>

                        <div className="billing-invoice-totals">
                          <div>
                            <span>Subtotal</span>

                            <strong>
                              {formatMoney(
                                selectedInvoice.subtotal
                              )}
                            </strong>
                          </div>

                          <div className="billing-total-row">
                            <span>Total</span>

                            <strong>
                              {formatMoney(
                                selectedInvoice.total
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>Paid</span>

                            <strong>
                              {formatMoney(
                                selectedInvoice.amount_paid
                              )}
                            </strong>
                          </div>

                          <div className="billing-balance-row">
                            <span>Balance</span>

                            <strong>
                              {formatMoney(
                                selectedInvoice.balance_due
                              )}
                            </strong>
                          </div>
                        </div>
                      </div>

                      {selectedInvoice.payments?.length > 0 && (
                        <div className="billing-payment-history">
                          <h3>Payment History</h3>
                          <div className="billing-modal-table-wrap">
                            <table className="billing-modal-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Method</th>
                                  <th>Reference</th>
                                  <th>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedInvoice.payments.map((payment) => (
                                  <tr key={payment.id}>
                                    <td>{formatDate(payment.payment_date)}</td>
                                    <td>{formatStatus(payment.payment_method)}</td>
                                    <td>{payment.payment_reference || "-"}</td>
                                    <td>{formatMoney(payment.amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {paymentSuccess && (
                        <div className="billing-payment-success">
                          {paymentSuccess}
                        </div>
                      )}

                      {paymentError && (
                        <div className="billing-error billing-payment-error">
                          {paymentError}
                        </div>
                      )}

                      {paymentFormOpen && (
                        <form className="billing-payment-form" onSubmit={recordPayment}>
                          <div className="billing-payment-form-header">
                            <div>
                              <h3>Record Payment</h3>
                              <p>Balance due: {formatMoney(selectedInvoice.balance_due)}</p>
                            </div>
                            <button
                              type="button"
                              className="billing-payment-cancel"
                              onClick={() => {
                                setPaymentFormOpen(false);
                                setPaymentError("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>

                          <div className="billing-payment-grid">
                            <label>
                              <span>Amount</span>
                              <input
                                type="number"
                                name="amount"
                                min="0.01"
                                max={Number(selectedInvoice.balance_due || 0).toFixed(2)}
                                step="0.01"
                                value={paymentForm.amount}
                                onChange={handlePaymentChange}
                                required
                              />
                            </label>

                            <label>
                              <span>Payment date</span>
                              <input
                                type="date"
                                name="payment_date"
                                value={paymentForm.payment_date}
                                onChange={handlePaymentChange}
                                required
                              />
                            </label>

                            <label>
                              <span>Payment method</span>
                              <select
                                name="payment_method"
                                value={paymentForm.payment_method}
                                onChange={handlePaymentChange}
                                required
                              >
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="direct_debit">Direct Debit</option>
                                <option value="other">Other</option>
                              </select>
                            </label>

                            <label>
                              <span>Reference (optional)</span>
                              <input
                                type="text"
                                name="payment_reference"
                                maxLength="200"
                                value={paymentForm.payment_reference}
                                onChange={handlePaymentChange}
                                placeholder="Bank or payment reference"
                              />
                            </label>

                            <label className="billing-payment-notes-field">
                              <span>Notes (optional)</span>
                              <textarea
                                name="notes"
                                rows="3"
                                value={paymentForm.notes}
                                onChange={handlePaymentChange}
                                placeholder="Optional payment note"
                              />
                            </label>
                          </div>

                          <button
                            type="submit"
                            className="billing-save-payment-btn"
                            disabled={paymentSaving}
                          >
                            {paymentSaving ? "Recording Payment..." : "Record Payment"}
                          </button>
                        </form>
                      )}

                      {statusActionSuccess && (
                        <div className="billing-payment-success">
                          {statusActionSuccess}
                        </div>
                      )}

                      {statusActionError && (
                        <div className="billing-error billing-payment-error">
                          {statusActionError}
                        </div>
                      )}

                      {pdfError && (
                        <div className="billing-error billing-pdf-error">
                          {pdfError}
                        </div>
                      )}

                      <div className="billing-modal-actions">
                        {selectedInvoice.status === "draft" &&
                          !paymentFormOpen && (
                            <button
                              type="button"
                              className="billing-mark-sent-btn"
                              onClick={markInvoiceAsSent}
                              disabled={statusActionSaving}
                            >
                              {statusActionSaving ? "Updating..." : "Mark as Sent"}
                            </button>
                          )}

                        {selectedInvoice.status === "draft" &&
                          !paymentFormOpen && (
                            <button
                              type="button"
                              className="billing-edit-invoice-btn"
                              onClick={startEditInvoice}
                            >
                              Edit Invoice
                            </button>
                          )}

                        {["draft", "sent"].includes(selectedInvoice.status) &&
                          !paymentFormOpen && (
                            <button
                              type="button"
                              className="billing-cancel-invoice-btn"
                              onClick={cancelInvoice}
                              disabled={statusActionSaving}
                            >
                              Cancel Invoice
                            </button>
                          )}

                        {Number(selectedInvoice.balance_due || 0) > 0.005 &&
                          selectedInvoice.status !== "cancelled" &&
                          !paymentFormOpen && (
                            <button
                              type="button"
                              className="billing-record-payment-btn"
                              onClick={openPaymentForm}
                            >
                              Record Payment
                            </button>
                          )}

                        <button
                          type="button"
                          className="billing-download-btn"
                          onClick={
                            downloadInvoicePdf
                          }
                          disabled={pdfLoading}
                        >
                          {pdfLoading
                            ? "Preparing PDF..."
                            : "Download PDF"}
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BillingDashboardPage;
